import React, {
  useEffect,
  useState,
} from "react";

import {
  FaSave,
  FaTimes,
} from "react-icons/fa";

import {
  toast,
} from "react-toastify";

import Button from "../../../../components/Button/Button";

import {
  getActeurById,
  updateActeur,
} from "../../../../services/acteursServices";

import {
  EMPTY_ACTEUR,
} from "./../data/acteurAdmin.constants";

import {
  buildActeurPayload,
  toActeurForm,
} from "../utils/acteursAdmin.utils";

import UserLinkSelect from "../ui/UserLinkSelect";

import ActeurFormFields from "./ActeurFormFields";

export default function EditActeurModal({
  isOpen,
  onClose,
  acteur,
  refreshList,
  users = [],
}) {
  const [
    acteurData,
    setActeurData,
  ] = useState(EMPTY_ACTEUR);

  const [
    selectedUserId,
    setSelectedUserId,
  ] = useState("");

  const [
    isLoading,
    setIsLoading,
  ] = useState(false);

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  const acteurId = acteur?.id;

  const linkedUsers =
    Array.isArray(acteur?.Users)
      ? acteur.Users
      : [];

  /*
   * Chargement des informations détaillées
   * de l'acteur.
   *
   * Les utilisateurs liés proviennent déjà
   * de l'objet acteur transmis par le tableau.
   */
  useEffect(() => {
    if (!isOpen || !acteurId) {
      return undefined;
    }

    let isActive = true;

    const loadActeur = async () => {
      try {
        setIsLoading(true);
        setSelectedUserId("");

        const data =
          await getActeurById(
            acteurId,
          );

        if (isActive) {
          setActeurData(
            toActeurForm(data),
          );
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement de l’acteur :",
          error,
        );

        toast.error(
          "Erreur lors de la récupération de l’acteur",
        );
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadActeur();

    return () => {
      isActive = false;
    };
  }, [isOpen, acteurId]);

  useEffect(() => {
    if (!isOpen) {
      setActeurData(EMPTY_ACTEUR);
      setSelectedUserId("");
      setIsLoading(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    setActeurData(
      (previousData) => ({
        ...previousData,
        [name]: value,
      }),
    );
  };

  const handleClose = () => {
    if (isSubmitting) return;

    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      isSubmitting ||
      !acteurId
    ) {
      return;
    }

    try {
      setIsSubmitting(true);

      const payload =
        buildActeurPayload(
          acteurData,
        );

      /*
       * On ne transmet user_id que si
       * l'acteur n'est pas encore lié.
       */
      if (
        !linkedUsers.length &&
        selectedUserId
      ) {
        payload.user_id = Number(
          selectedUserId,
        );
      }

      await updateActeur(
        acteurId,
        payload,
      );

      if (
        !linkedUsers.length &&
        selectedUserId
      ) {
        toast.success(
          "Acteur mis à jour et lié à l’utilisateur",
        );
      } else {
        toast.success(
          "Acteur mis à jour avec succès",
        );
      }

      await refreshList();

      onClose();
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de l’acteur :",
        error,
      );

      toast.error(
        error?.response?.data?.message ||
          "Erreur lors de la mise à jour de l’acteur",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-2 py-3 backdrop-blur-sm">
      <div className="flex max-h-[calc(100vh-24px)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900">
        {/* En-tête */}
        <div className="flex shrink-0 items-center justify-between bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 px-5 py-4">
          <div>
            <h3 className="text-base font-bold uppercase tracking-[0.18em] text-white sm:text-lg">
              Modifier un acteur
            </h3>

            <p className="mt-1 text-xs text-white/85">
              Mettez à jour sa fiche
              et associez un compte
              s’il n’en possède pas.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/20 text-white transition hover:bg-black/30 disabled:opacity-50"
          >
            <FaTimes />
          </button>
        </div>

        {isLoading ? (
          <div className="px-6 py-10 text-center text-sm text-slate-600 dark:text-slate-200">
            Chargement des informations
            de l’acteur...
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
              <ActeurFormFields
                data={acteurData}
                onChange={
                  handleChange
                }
              />

              <UserLinkSelect
                users={users}
                value={selectedUserId}
                onChange={
                  setSelectedUserId
                }
                linkedUsers={
                  linkedUsers
                }
                disabled={
                  isSubmitting
                }
              />
            </div>

            {/* Actions */}
            <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 bg-white px-5 py-4 dark:border-slate-700 dark:bg-slate-900">
              <Button
                text="Annuler"
                icon={FaTimes}
                type="button"
                onClick={
                  handleClose
                }
                className="btn-ghost gap-2 !px-4 !py-2"
                disabled={
                  isSubmitting
                }
              />

              <Button
                text={
                  isSubmitting
                    ? "Enregistrement..."
                    : "Mettre à jour"
                }
                icon={FaSave}
                type="submit"
                className="btn-primary gap-2 !px-4 !py-2"
                disabled={
                  isSubmitting
                }
              />
            </div>
          </form>
        )}
      </div>
    </div>
  );
}